import { useState } from 'react';
import { bulkChangeStatus } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BulkStatusDialog = ({ open, onOpenChange, unitIds, statusAction, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    reason: '', reason_detail: '', buyer_reference: '', sale_reference: '',
    block_hours: 24,
    contact: { full_name: '', primary_phone: '', email: '', address: '' },
    contact_id: null,
  });
  const [contactSuggestions, setContactSuggestions] = useState([]);

  const searchContactDuplicates = async (phone) => {
    if (!phone || phone.length < 4) { setContactSuggestions([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/v1/contacts/search-duplicates?phone=${encodeURIComponent(phone)}`, { credentials: 'include' });
      if (res.ok) setContactSuggestions(await res.json());
    } catch (err) { console.error('Contact search failed:', err); }
  };

  const selectExistingContact = (contact) => {
    setForm(prev => ({
      ...prev, contact_id: contact.contact_id,
      contact: { full_name: contact.full_name, primary_phone: contact.primary_phone, email: contact.email || '', address: contact.address_line_1 || '' }
    }));
    setContactSuggestions([]);
  };

  const handleSubmit = async () => {
    if (['soft_blocked', 'booked', 'sold'].includes(statusAction)) {
      if (!form.contact.full_name.trim() || !form.contact.primary_phone.trim()) {
        toast.error('Contact name and phone are required');
        return;
      }
    }
    if (statusAction === 'booked' && !form.buyer_reference.trim()) { toast.error('Booking reference is required'); return; }
    if (statusAction === 'sold' && !form.sale_reference.trim()) { toast.error('Sale reference is required'); return; }

    setSubmitting(true);
    try {
      const payload = {
        unit_ids: unitIds,
        status: statusAction,
        reason: form.reason || statusAction,
        reason_detail: form.reason_detail,
        buyer_reference: form.buyer_reference,
        sale_reference: form.sale_reference,
        block_hours: parseInt(form.block_hours) || 24,
      };
      if (['soft_blocked', 'booked', 'sold'].includes(statusAction)) {
        payload.contact = { ...form.contact, contact_id: form.contact_id || undefined };
      }

      const result = await bulkChangeStatus(payload);
      if (result.success > 0) {
        toast.success(`${result.success} unit(s) updated successfully`);
      }
      if (result.failed > 0) {
        toast.warning(`${result.failed} unit(s) failed to update`);
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error(error.message || 'Bulk status change failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (v) {
      setForm({
        reason: '', reason_detail: '', buyer_reference: '', sale_reference: '',
        block_hours: 24,
        contact: { full_name: '', primary_phone: '', email: '', address: '' },
        contact_id: null,
      });
      setContactSuggestions([]);
    }
    onOpenChange(v);
  };

  const titleMap = {
    available: 'Release Units',
    soft_blocked: 'Soft Block Units',
    booked: 'Book Units',
    sold: 'Mark Units as Sold',
    on_hold: 'Put Units On Hold',
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{titleMap[statusAction] || 'Change Status'}</DialogTitle>
          <DialogDescription>
            Apply to {unitIds.length} selected unit{unitIds.length !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {['available', 'on_hold'].includes(statusAction) && (
            <div className="p-3 rounded-lg bg-muted/30 border">
              <p className="text-sm">
                {statusAction === 'available'
                  ? `This will release ${unitIds.length} unit(s) back to available status. Any active soft blocks or holds will be cleared.`
                  : `This will put ${unitIds.length} unit(s) on hold. Only admin/management can release held units.`}
              </p>
            </div>
          )}
          {['soft_blocked', 'booked', 'sold'].includes(statusAction) && (
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
              <p className="text-xs font-semibold text-foreground">
                {statusAction === 'soft_blocked' ? 'Blocker' : statusAction === 'booked' ? 'Booker' : 'Purchaser'} Contact *
              </p>
              <p className="text-[10px] text-muted-foreground">This contact will be linked to all selected units</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name *</Label>
                  <Input value={form.contact.full_name} onChange={(e) => setForm(prev => ({ ...prev, contact: { ...prev.contact, full_name: e.target.value }, contact_id: null }))} placeholder="Contact name" className="h-8 text-sm" data-testid="bulk-contact-name" />
                </div>
                <div className="space-y-1 relative">
                  <Label className="text-xs">Phone *</Label>
                  <Input value={form.contact.primary_phone} onChange={(e) => { setForm(prev => ({ ...prev, contact: { ...prev.contact, primary_phone: e.target.value }, contact_id: null })); searchContactDuplicates(e.target.value); }} placeholder="+91 98765 43210" className="h-8 text-sm" data-testid="bulk-contact-phone" />
                  {contactSuggestions.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg max-h-32 overflow-y-auto">
                      <p className="text-[10px] text-muted-foreground px-2 py-1 border-b">Existing contacts:</p>
                      {contactSuggestions.map(c => (
                        <button key={c.contact_id} className="w-full text-left px-2 py-1.5 hover:bg-muted text-xs flex justify-between" onClick={() => selectExistingContact(c)}>
                          <span className="font-medium">{c.full_name}</span>
                          <span className="text-muted-foreground">{c.primary_phone}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input value={form.contact.email} onChange={(e) => setForm(prev => ({ ...prev, contact: { ...prev.contact, email: e.target.value } }))} placeholder="email@example.com" className="h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Address</Label>
                  <Input value={form.contact.address} onChange={(e) => setForm(prev => ({ ...prev, contact: { ...prev.contact, address: e.target.value } }))} className="h-8 text-sm" />
                </div>
              </div>
              {form.contact_id && <p className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Linked to existing contact</p>}
            </div>
          )}

          {statusAction === 'soft_blocked' && (
            <>
              <div className="space-y-2">
                <Label>Block Duration (hours)</Label>
                <Select value={form.block_hours.toString()} onValueChange={(v) => setForm(prev => ({ ...prev, block_hours: parseInt(v) }))}>
                  <SelectTrigger data-testid="bulk-block-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2, 4, 8, 12, 24, 48].map(h => <SelectItem key={h} value={h.toString()}>{h} hours</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <Select value={form.reason} onValueChange={(v) => setForm(prev => ({ ...prev, reason: v }))}>
                  <SelectTrigger data-testid="bulk-block-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer_visit">Customer Visit</SelectItem>
                    <SelectItem value="negotiation">Price Negotiation</SelectItem>
                    <SelectItem value="documentation">Documentation Pending</SelectItem>
                    <SelectItem value="payment_pending">Payment Pending</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {statusAction === 'booked' && (
            <div className="space-y-2">
              <Label>Booking Reference *</Label>
              <Input value={form.buyer_reference} onChange={(e) => setForm(prev => ({ ...prev, buyer_reference: e.target.value }))} placeholder="e.g., BOOK-2024-001" data-testid="bulk-booking-reference" />
            </div>
          )}

          {statusAction === 'sold' && (
            <div className="space-y-2">
              <Label>Sale / Allotment Reference *</Label>
              <Input value={form.sale_reference} onChange={(e) => setForm(prev => ({ ...prev, sale_reference: e.target.value }))} placeholder="e.g., SALE-2024-001" data-testid="bulk-sale-reference" />
            </div>
          )}

          <div className="space-y-2">
            <Label>Additional Notes</Label>
            <Textarea value={form.reason_detail} onChange={(e) => setForm(prev => ({ ...prev, reason_detail: e.target.value }))} placeholder="Any additional details..." rows={2} data-testid="bulk-status-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="confirm-bulk-status">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Apply to {unitIds.length} Units
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
